import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Modal } from "react-bootstrap";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import "./image.css";

const ImageCrop = forwardRef(({
  url,
  cropWidth = 500,
  cropHeight = 500,
  onCropComplete,
  show = false,
  onHide,
  onCropSave,
  onCropCancel,
  title = "Crop your photo",
  confirmButtonText = "Add Photo",
  cancelButtonText = "Cancel"
}, ref) => {
  const [image, setImage] = useState(url);
  const [cropError, setCropError] = useState(null);
  const cropperRef = useRef(null);

  useEffect(() => {
    setImage(url);
  }, [url]);

  const getCroppedImg = async () => {
    if (!cropperRef.current) return null;

    const cropper = cropperRef.current.cropper;
    const canvas = document.createElement('canvas');

    canvas.width = cropWidth;
    canvas.height = cropHeight;

    const ctx = canvas.getContext('2d');
    const cropData = cropper.getData();
    const imageElement = cropper.image;

    const imageObj = new Image();
    imageObj.crossOrigin = 'anonymous';
    imageObj.src = imageElement.src;

    return new Promise((resolve, reject) => {
      imageObj.onload = () => {
        ctx.drawImage(
          imageElement,
          cropData.x,
          cropData.y,
          cropData.width,
          cropData.height,
          0,
          0,
          canvas.width,
          canvas.height
        );

        canvas.toBlob(
          blob => {
            if (!blob) {
              reject(new Error('Canvas is empty'));
              return;
            }
            resolve(blob);
          },
          'image/jpeg',
          1.0
        );
      };

      imageObj.onerror = error => {
        reject(error);
      };
    });
  };

  const handleSubmit = async () => {
    try {
      const croppedImage = await getCroppedImg();
      if (croppedImage && typeof onCropComplete === 'function') {
        onCropComplete(croppedImage);
      }
      return croppedImage;
    } catch (error) {
      console.error('Failed to crop image:', error);
      return null;
    }
  };

  const handleCropSave = async () => {
    try {
      setCropError(null);
      const croppedBlob = await handleSubmit();
      if (!croppedBlob) {
        throw new Error('Failed to crop image. Please try again.');
      }
      if (onCropSave) {
        onCropSave(croppedBlob);
      }
    } catch (error) {
      setCropError(error.message);
    }
  };

  const handleCropCancel = () => {
    setCropError(null);
    if (onCropCancel) {
      onCropCancel();
    }
    if (onHide) {
      onHide();
    }
  };

  useImperativeHandle(ref, () => ({
    handleSubmit,
    handleCropSave,
    handleCropCancel,
    setCropError
  }));

  if (!show) {
    return null;
  }

  return (
    <Modal
      centered
      className="customer-modal-without-header-footer crop-modal"
      show={show}
      onHide={handleCropCancel}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="crop-modal-body">
        {cropError && (
          <div className="crop-error-message">
            <p>{cropError}</p>
          </div>
        )}

        <div
          className="crop-container"
          style={{
            width: `${Math.min(cropWidth + 40, 500)}px`,
            height: `${Math.min(cropHeight + 40, 400)}px`,
            maxWidth: '85vw',
            maxHeight: '60vh'
          }}
        >
          <Cropper
            ref={cropperRef}
            style={{
              width: `${Math.min(cropWidth, 450)}px`,
              height: `${Math.min(cropHeight, 350)}px`,
              maxWidth: '100%',
              maxHeight: '100%'
            }}
            initialAspectRatio={cropWidth / cropHeight}
            src={image}
            viewMode={1}
            guides={true}
            background={false}
            responsive={false}
            autoCropArea={1}
            checkOrientation={false}
            dragMode="move"

            // Lock the crop box size to parent dimensions:
            cropBoxResizable={false}
            cropBoxMovable={false}
            center={true}
            minCropBoxWidth={cropWidth}
            minCropBoxHeight={cropHeight}
            maxCropBoxWidth={cropWidth}
            maxCropBoxHeight={cropHeight}

            aspectRatio={cropWidth / cropHeight}
          />
        </div>
      </Modal.Body>

      <Modal.Footer className="crop-modal-footer">
        <div className="d-flex justify-content-between w-100 gap-2">
          <button
            className="global-cancel-button"
            onClick={handleCropCancel}
          >
            {cancelButtonText}
          </button>
          <button
            className="global-next-btn"
            onClick={handleCropSave}
          >
            {confirmButtonText}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
});

export default ImageCrop;